import { useState, useMemo, useEffect, useCallback } from 'react';
import Layout from '@/components/feature/Layout';
import { useTagColors } from '@/hooks/useTagColors';
import {
  fetchBusinessTags, fetchSourceTags, fetchTagStats,
  createBusinessTag, updateBusinessTag, deleteBusinessTag,
  createSourceTag, updateSourceTag, deleteSourceTag,
  type TagInfo, type TagStats,
} from '@/api/tags';

const TAG_COLOR_PRESETS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#d946ef', '#ec4899', '#f43f5e', '#6b7280',
];

export default function TagsPage() {
  const [activeTab, setActiveTab] = useState<'business' | 'source'>('business');
  const [businessTagInfos, setBusinessTagInfos] = useState<TagInfo[]>([]);
  const [sourceTagInfos, setSourceTagInfos] = useState<TagInfo[]>([]);
  const [tagStats, setTagStats] = useState<TagStats>({ businessTotal: 0, sourceTotal: 0, companyTotal: 0, totalUsages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTag, setEditingTag] = useState<{ oldName: string; type: 'business' | 'source' } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ name: string; type: 'business' | 'source'; count: number } | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [colorPickerTag, setColorPickerTag] = useState<string | null>(null);
  const { colors: tagColors, setTagColor, removeTagColor } = useTagColors();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [businessTags, sourceTags, stats] = await Promise.all([
        fetchBusinessTags(),
        fetchSourceTags(),
        fetchTagStats(),
      ]);
      setBusinessTagInfos(businessTags);
      setSourceTagInfos(sourceTags);
      setTagStats(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载标签数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentTags = activeTab === 'business' ? businessTagInfos : sourceTagInfos;
  const totalUsages = currentTags.reduce((sum, t) => sum + t.count, 0);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 2500);
  };

  const handleAddTag = async () => {
    const trimmed = newTagValue.trim();
    if (!trimmed) return;

    try {
      if (activeTab === 'business') {
        const newTag = await createBusinessTag(trimmed);
        setBusinessTagInfos((prev) => [...prev, newTag]);
      } else {
        const newTag = await createSourceTag(trimmed);
        setSourceTagInfos((prev) => [...prev, newTag]);
      }
      setNewTagValue('');
      showMessage('success', `标签"${trimmed}"添加成功`);
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '添加失败');
    }
  };

  const handleStartEdit = (tag: TagInfo) => {
    setEditingTag({ oldName: tag.name, type: activeTab });
    setEditValue(tag.name);
  };

  const handleSaveEdit = async () => {
    if (!editingTag) return;
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === editingTag.oldName) {
      setEditingTag(null);
      return;
    }
    try {
      if (editingTag.type === 'business') {
        const updated = await updateBusinessTag(editingTag.oldName, trimmed);
        setBusinessTagInfos((prev) => prev.map((t) => (t.name === editingTag.oldName ? updated : t)));
      } else {
        const updated = await updateSourceTag(editingTag.oldName, trimmed);
        setSourceTagInfos((prev) => prev.map((t) => (t.name === editingTag.oldName ? updated : t)));
      }
      setEditingTag(null);
      showMessage('success', `标签已更新为"${trimmed}"`);
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '更新失败');
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    try {
      if (showDeleteConfirm.type === 'business') {
        await deleteBusinessTag(showDeleteConfirm.name);
        setBusinessTagInfos((prev) => prev.filter((t) => t.name !== showDeleteConfirm.name));
      } else {
        await deleteSourceTag(showDeleteConfirm.name);
        setSourceTagInfos((prev) => prev.filter((t) => t.name !== showDeleteConfirm.name));
      }
      showMessage('success', `标签"${showDeleteConfirm.name}"已删除, 影响 ${showDeleteConfirm.count} 家企业`);
    } catch (err) {
      showMessage('error', err instanceof Error ? err.message : '删除失败');
    }
    setShowDeleteConfirm(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (editingTag) {
        handleSaveEdit();
      } else {
        handleAddTag();
      }
    }
    if (e.key === 'Escape') {
      setEditingTag(null);
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">标签管理</h1>
            <p className="text-sm text-foreground-500 mt-1">管理业务标签和来源标签，查看使用情况</p>
          </div>
        </div>

        {/* Success/Error message */}
        {message && (
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
              message.type === 'success'
                ? 'bg-accent-50 text-accent-700 border border-accent-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            <i className={message.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            {message.text}
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => { setError(null); loadData(); }}
              className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              重试
            </button>
          </div>
        )}

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-price-tag-3-line text-lg text-accent-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : tagStats.businessTotal}</p>
                <p className="text-xs text-foreground-500">业务标签总数</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-folder-2-line text-lg text-secondary-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : tagStats.sourceTotal}</p>
                <p className="text-xs text-foreground-500">来源标签总数</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-building-2-line text-lg text-primary-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : tagStats.companyTotal}</p>
                <p className="text-xs text-foreground-500">关联企业总数</p>
              </div>
            </div>
          </div>
          <div className="bg-background-50 rounded-xl p-4 border border-background-200/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0">
                <i className="ri-links-line text-lg text-foreground-600"></i>
              </div>
              <div>
                <p className="text-2xl font-heading font-bold text-foreground-900">{loading ? '-' : totalUsages}</p>
                <p className="text-xs text-foreground-500">当前标签引用次数</p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 py-16 text-center">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
              <p className="text-sm text-foreground-500">加载标签数据...</p>
            </div>
          </div>
        )}

        {/* Tabs + Add */}
        {!loading && (
        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="border-b border-background-200/70 px-4 md:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setActiveTab('business')}
                  className={`px-4 py-3 text-sm font-medium transition-colors cursor-pointer border-b-2 whitespace-nowrap ${
                    activeTab === 'business'
                      ? 'text-accent-600 border-accent-500'
                      : 'text-foreground-500 border-transparent hover:text-foreground-700'
                  }`}
                >
                  <i className="ri-price-tag-3-line mr-1.5"></i>
                  业务标签
                  <span className="ml-1.5 px-1.5 py-0.5 rounded bg-background-100 text-xs text-foreground-500">
                    {businessTagInfos.length}
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('source')}
                  className={`px-4 py-3 text-sm font-medium transition-colors cursor-pointer border-b-2 whitespace-nowrap ${
                    activeTab === 'source'
                      ? 'text-secondary-600 border-secondary-500'
                      : 'text-foreground-500 border-transparent hover:text-foreground-700'
                  }`}
                >
                  <i className="ri-folder-2-line mr-1.5"></i>
                  来源标签
                  <span className="ml-1.5 px-1.5 py-0.5 rounded bg-background-100 text-xs text-foreground-500">
                    {sourceTagInfos.length}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Add new tag */}
          <div className="px-4 md:px-6 py-4 border-b border-background-200/70">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <i className="ri-add-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={activeTab === 'business' ? '输入新业务标签名称，回车添加' : '输入新来源标签名称，回车添加'}
                  className="w-full h-10 pl-8 pr-4 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
              <button
                onClick={handleAddTag}
                disabled={!newTagValue.trim()}
                className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                添加标签
              </button>
            </div>
          </div>

          {/* Tag list */}
          <div className="divide-y divide-background-200/70">
            {currentTags.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">暂无{activeTab === 'business' ? '业务' : '来源'}标签</p>
                  <p className="text-xs text-foreground-400">在上方输入框中添加第一个标签</p>
                </div>
              </div>
            ) : (
              currentTags.map((tag) => (
                <div
                  key={tag.name}
                  className="flex items-center justify-between px-4 md:px-6 py-3.5 hover:bg-background-50/80 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {editingTag?.oldName === tag.name && editingTag?.type === activeTab ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          className="w-48 h-9 px-3 rounded-lg border border-primary-400 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-100 transition-all"
                        />
                        <button
                          onClick={handleSaveEdit}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-check-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => setEditingTag(null)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                            activeTab === 'business'
                              ? 'bg-accent-50 text-accent-700'
                              : 'bg-secondary-50 text-secondary-700'
                          }`}
                        >
                          {tagColors[tag.name] && (
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: tagColors[tag.name] }}
                            ></span>
                          )}
                          {tag.name}
                        </span>
                        <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                          <i className="ri-building-2-line"></i>
                          <span>
                            被 <strong className="text-foreground-700">{tag.count}</strong> 家企业使用
                          </span>
                        </div>
                        {tag.count > 0 && (
                          <div className="hidden lg:flex items-center gap-1 text-xs text-foreground-400 overflow-hidden">
                            <span className="whitespace-nowrap">如：</span>
                            <span className="truncate">{
                              tag.companies.slice(0, 3).join('、')
                            }{tag.companies.length > 3 ? ` 等${tag.companies.length}家` : ''}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {(!editingTag || editingTag.oldName !== tag.name || editingTag.type !== activeTab) && (
                    <div className="flex items-center gap-1 flex-shrink-0 ml-3">
                      <div className="relative">
                        <button
                          onClick={() => setColorPickerTag(colorPickerTag === tag.name ? null : tag.name)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                          title="自定义颜色"
                        >
                          <i className="ri-palette-line text-sm"></i>
                        </button>
                        {colorPickerTag === tag.name && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setColorPickerTag(null)} />
                            <div className="absolute top-full mt-1 right-0 z-20 bg-background-50 rounded-xl border border-background-200/70 p-3 w-[208px]">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-foreground-600">选择颜色</span>
                                {tagColors[tag.name] && (
                                  <button
                                    onClick={() => {
                                      removeTagColor(tag.name);
                                      setColorPickerTag(null);
                                    }}
                                    className="text-[10px] text-foreground-400 hover:text-red-500 cursor-pointer whitespace-nowrap"
                                  >
                                    清除
                                  </button>
                                )}
                              </div>
                              <div className="grid grid-cols-8 gap-1.5">
                                {TAG_COLOR_PRESETS.map((color) => (
                                  <button
                                    key={color}
                                    onClick={() => {
                                      setTagColor(tag.name, color);
                                      setColorPickerTag(null);
                                    }}
                                    className={`w-5 h-5 rounded-full cursor-pointer transition-transform hover:scale-125 hover:shadow-sm ${
                                      tagColors[tag.name] === color ? 'ring-2 ring-offset-1 ring-foreground-900' : ''
                                    }`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                  />
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => handleStartEdit(tag)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                        title="编辑标签"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm({ name: tag.name, type: activeTab, count: tag.count })}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                        title="删除标签"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div className="absolute inset-0 bg-foreground-950/40" />
          <div
            className="relative bg-background-50 rounded-2xl w-full max-w-[420px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-lg text-red-500"></i>
                </div>
                <div>
                  <h3 className="text-base font-heading font-bold text-foreground-900">确认删除标签</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">此操作无法撤销</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-4 mb-2">
                <p className="text-sm text-foreground-700">
                  将要删除标签 <strong className="text-foreground-900">"{showDeleteConfirm.name}"</strong>
                </p>
                {showDeleteConfirm.count > 0 && (
                  <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                    <i className="ri-alert-line"></i>
                    该标签当前被 <strong>{showDeleteConfirm.count}</strong> 家企业使用，删除后将从所有企业中移除
                  </p>
                )}
              </div>
            </div>
            <div className="border-t border-background-200/70 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2.5 bg-red-500 hover:bg-red-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}